import { supabase, unwrap } from '@/services/supabase/client'
import type {
  DiscoverEntry,
  FollowRequestEntry,
  ProfileCard,
  RelationEntry,
  UserSearchResult,
} from '@/types/models'

export async function getProfileCard(opts: {
  username?: string
  userId?: string
}): Promise<ProfileCard | null> {
  const rows = unwrap(
    await supabase.rpc('get_profile_card', {
      p_username: opts.username ?? null,
      p_user_id: opts.userId ?? null,
    }),
  ) as ProfileCard[]
  return rows[0] ?? null
}

export async function searchUsers(query: string, limit = 20): Promise<UserSearchResult[]> {
  return unwrap(
    await supabase.rpc('search_users', { p_query: query, p_limit: limit }),
  ) as UserSearchResult[]
}

export async function discoverTop(limit = 100, offset = 0): Promise<DiscoverEntry[]> {
  return unwrap(
    await supabase.rpc('discover_top', { p_limit: limit, p_offset: offset }),
  ) as DiscoverEntry[]
}

export async function requestFollow(targetId: string, message?: string): Promise<string> {
  return unwrap(
    await supabase.rpc('request_follow', {
      p_target: targetId,
      p_message: message?.trim() || null,
    }),
  ) as string
}

export async function respondFollowRequest(requestId: string, accept: boolean): Promise<void> {
  unwrap(await supabase.rpc('respond_follow_request', {
    p_request_id: requestId,
    p_accept: accept,
  }))
}

export async function cancelFollowRequest(requestId: string): Promise<void> {
  unwrap(await supabase.rpc('cancel_follow_request', { p_request_id: requestId }))
}

export async function unfollow(targetId: string): Promise<void> {
  unwrap(await supabase.rpc('unfollow', { p_target: targetId }))
}

export async function removeFollower(followerId: string): Promise<void> {
  unwrap(await supabase.rpc('remove_follower', { p_follower: followerId }))
}

export async function setCloseFriend(followerId: string, on: boolean): Promise<void> {
  unwrap(await supabase.rpc('set_close_friend', { p_follower: followerId, p_on: on }))
}

export async function blockUser(targetId: string): Promise<void> {
  unwrap(await supabase.rpc('block_user', { p_target: targetId }))
}

export async function unblockUser(targetId: string): Promise<void> {
  unwrap(await supabase.rpc('unblock_user', { p_target: targetId }))
}

export type RelationList = 'followers' | 'following' | 'close_friends' | 'blocked'

export async function listRelations(kind: RelationList): Promise<RelationEntry[]> {
  return unwrap(await supabase.rpc('list_relations', { p_kind: kind })) as RelationEntry[]
}

export async function listFollowRequests(): Promise<FollowRequestEntry[]> {
  return unwrap(await supabase.rpc('list_follow_requests', {})) as FollowRequestEntry[]
}

export async function usernameAvailable(username: string): Promise<boolean> {
  return unwrap(
    await supabase.rpc('username_available', { p_username: username }),
  ) as boolean
}

/** The daily treat on Descubrir: 10 points, claimable every 24 hours. */
export async function getDailyBonus(): Promise<{ can_claim: boolean; next_at: string | null }> {
  return unwrap(await supabase.rpc('get_daily_bonus', {})) as {
    can_claim: boolean
    next_at: string | null
  }
}

export async function claimDailyBonus(): Promise<{ claimed: number; next_at: string }> {
  return unwrap(await supabase.rpc('claim_daily_bonus', {})) as {
    claimed: number
    next_at: string
  }
}

/** My live score, rank, and what today has earned so far. */
export interface MyPoints {
  score: number
  rank: number | null
  today: { points: number; new_cats: number; sightings: number; photos: number; bonus: number }
}

export async function getMyPoints(): Promise<MyPoints> {
  return unwrap(await supabase.rpc('get_my_points', {})) as MyPoints
}

/** The soft streak: consecutive days with the treat claimed, one weekly slip forgiven. */
export interface Streak {
  days: number
  today_claimed: boolean
  /** A streak of this length just ended; say a kind goodbye once. */
  rested_from: number | null
}

export async function getStreak(): Promise<Streak> {
  return unwrap(await supabase.rpc('get_streak', {})) as Streak
}
